import { BrowserQRCodeReader } from '@zxing/browser';

/**
 * What a photo held, as far as the QR decoder can tell (CWS-1908).
 * - decoded: a QR was read; `text` is its content.
 * - unreadable: the decoder found a QR's finder patterns but could not read it, so the
 *   attendant pointed at a QR that is damaged, blurred, glared or decorative.
 * - none: no QR in the picture; treat it as a plate photo.
 */
export type QrImageDecode =
  { kind: 'decoded'; text: string } | { kind: 'unreadable' } | { kind: 'none' };

/** ZXing error kinds that mean "a QR was there, it just could not be read". */
const UNREADABLE_QR_ERROR_KINDS = new Set(['ChecksumException', 'FormatException']);

/**
 * Classify a ZXing decode failure. ZXing exceptions expose `getKind()` with a stable name,
 * which survives minification where a class name would not; anything else is "no QR".
 */
export function classifyQrDecodeError(error: unknown): 'unreadable' | 'none' {
  const kind =
    typeof (error as { getKind?: () => string } | null)?.getKind === 'function'
      ? (error as { getKind: () => string }).getKind()
      : '';
  return UNREADABLE_QR_ERROR_KINDS.has(kind) ? 'unreadable' : 'none';
}

/** Decode the first QR in an image file, saying whether a QR was present but unreadable. */
export async function decodeQrFromImageFile(file: File): Promise<QrImageDecode> {
  if (typeof URL === 'undefined' || !file) {
    return { kind: 'none' };
  }

  let objectUrl: string | null = null;
  try {
    objectUrl = URL.createObjectURL(file);
    const codeReader = new BrowserQRCodeReader();
    const result = await codeReader.decodeFromImageUrl(objectUrl);
    const text = result?.getText()?.trim();
    return text ? { kind: 'decoded', text } : { kind: 'none' };
  } catch (error) {
    return { kind: classifyQrDecodeError(error) };
  } finally {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

/** Decode the first QR code found in an image file, or null if none / decode fails. */
export async function tryDecodeQrFromImageFile(file: File): Promise<string | null> {
  const decoded = await decodeQrFromImageFile(file);
  return decoded.kind === 'decoded' ? decoded.text : null;
}
